"use client";

import { useState } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";

const treatmentTypes = [
{ id: "ivf", label: "IVF", icon: "🔬", desc: "In Vitro Fertilization" },
{ id: "iui", label: "IUI", icon: "💉", desc: "Intrauterine Insemination" },
{ id: "egg-donation", label: "Egg Donation", icon: "🥚", desc: "Donor Egg Cycle" },
{ id: "sperm-donation", label: "Sperm Donation", icon: "🔵", desc: "Donor Sperm Cycle" },
{ id: "surrogacy", label: "Surrogacy", icon: "💛", desc: "Gestational Carrier" },
{ id: "embryo-adoption", label: "Embryo Adoption", icon: "🌱", desc: "Frozen Embryo Transfer" },
{ id: "other", label: "Other", icon: "✨", desc: "Other Fertility Treatment" }];


const costBreakdown = [
{ label: "IVF Cycle (base)", amount: 12000 },
{ label: "Medications", amount: 4500 },
{ label: "Monitoring & Labs", amount: 2000 },
{ label: "Genetic Testing (PGT)", amount: 3000 },
{ label: "Embryo Freezing", amount: 1500 }];


const steps = ["Treatment", "Story", "Goal", "Privacy", "Preview"];

export default function CampaignFormInteractive() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTreatment, setSelectedTreatment] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState("");
  const [story, setStory] = useState("");
  const [goalAmount, setGoalAmount] = useState(20000);
  const [privacyLevel, setPrivacyLevel] = useState<"link" | "email" | "code">("link");
  const [showClinicBanner, setShowClinicBanner] = useState(true);
  const [photoUploaded, setPhotoUploaded] = useState(false);

  const progressPercent = currentStep / (steps.length - 1) * 100;

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="min-h-screen pt-20" style={{ background: "#FDF8F4" }}>
      {/* Clinic integration banner */}
      {showClinicBanner &&
      <div className="relative px-4 py-3 text-center text-sm font-medium"
      style={{ background: "linear-gradient(90deg, #D4EAD9, #EBF4FA)", color: "#1C2B35" }}>
          <span className="text-growth mr-2" aria-hidden="true">🏥</span>
          <strong>Coming Soon:</strong> Import your treatment costs directly from your fertility clinic quote.{" "}
          <a href="#" className="underline text-primary hover:text-primary-dark transition-colors">Learn more</a>
          <button
          onClick={() => setShowClinicBanner(false)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors"
          aria-label="Dismiss banner">
          
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      }

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left: Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div className="space-y-2">
              <span className="section-tag">Create Campaign</span>
              <h1 className="font-heading font-bold text-ink" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", lineHeight: 1.1 }}>
                Your journey starts here
              </h1>
              <p className="text-ink-muted">Build your private campaign in just a few steps.</p>
            </div>

            {/* Progress bar */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                {steps.map((step, i) =>
                <button
                  key={step}
                  onClick={() => i <= currentStep && setCurrentStep(i)}
                  className="flex flex-col items-center gap-1 group"
                  disabled={i > currentStep}
                  aria-current={i === currentStep ? "step" : undefined}>
                  
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  i < currentStep ? "bg-growth text-white" :
                  i === currentStep ? "bg-primary text-white shadow-warm-md" : "bg-warm-border text-ink-soft"}`
                  }>
                      {i < currentStep ?
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12" />
                        </svg> :
                    i + 1}
                    </div>
                    <span className={`text-2xs font-medium hidden sm:block transition-colors ${
                  i === currentStep ? "text-primary" : i < currentStep ? "text-growth" : "text-ink-soft"}`
                  }>{step}</span>
                  </button>
                )}
              </div>
              <div className="relative h-1.5 rounded-full overflow-hidden" style={{ background: "#EDE5DB" }}>
                <div className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%`, background: "linear-gradient(90deg, #2D6A8F, #5A8A6F)" }} />
              </div>
            </div>

            {/* Step content */}
            <div className="bg-white rounded-3xl p-8 border border-warm-border-light shadow-warm-sm">

              {/* STEP 0: Treatment Type */}
              {currentStep === 0 &&
              <div className="space-y-6">
                  <div>
                    <h2 className="font-heading font-semibold text-xl text-ink mb-1">What type of treatment?</h2>
                    <p className="text-sm text-ink-muted">Select your primary fertility treatment. This helps us tailor your campaign.</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {treatmentTypes.map((t) =>
                  <button
                    key={t.id}
                    onClick={() => setSelectedTreatment(t.id)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 group hover:border-primary hover:shadow-warm-sm ${
                    selectedTreatment === t.id ?
                    "border-primary bg-primary-pale shadow-warm-sm" :
                    "border-warm-border bg-warm-bg"}`
                    }>
                    
                        <span className="text-2xl mb-2 block" aria-hidden="true">{t.icon}</span>
                        <p className="font-heading font-semibold text-sm text-ink">{t.label}</p>
                        <p className="text-2xs text-ink-muted mt-0.5">{t.desc}</p>
                      </button>
                  )}
                  </div>
                  <div className="p-4 rounded-2xl" style={{ background: "#EBF4FA", border: "1px solid rgba(45,106,143,0.15)" }}>
                    <p className="text-xs text-primary font-medium flex items-start gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      Your treatment type is only visible to people you choose to share with. It's never shown publicly.
                    </p>
                  </div>
                </div>
              }

              {/* STEP 1: Story */}
              {currentStep === 1 &&
              <div className="space-y-6">
                  <div>
                    <h2 className="font-heading font-semibold text-xl text-ink mb-1">Tell your story</h2>
                    <p className="text-sm text-ink-muted">Share as much or as little as you're comfortable with. This is private — only your invitees will see it.</p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label htmlFor="campaign-name" className="text-sm font-medium text-ink">Campaign name</label>
                      <input
                      id="campaign-name"
                      type="text"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      placeholder="e.g., Sarah & Mike's IVF Journey"
                      className="input-field" />
                    
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="story" className="text-sm font-medium text-ink">Your story <span className="text-ink-soft font-normal">(optional)</span></label>
                      <textarea
                      id="story"
                      value={story}
                      onChange={(e) => setStory(e.target.value)}
                      placeholder="Share your journey — why you're pursuing this treatment, what it means to you, and how the support will help..."
                      rows={6}
                      className="input-field resize-none"
                      style={{ lineHeight: "1.6" }} />
                    
                      <p className="text-xs text-ink-soft">{story.length}/1000 characters</p>
                    </div>

                    {/* Photo upload */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-ink">Add a photo <span className="text-ink-soft font-normal">(optional)</span></label>
                      <button
                      onClick={() => setPhotoUploaded(!photoUploaded)}
                      className={`w-full h-32 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-all duration-200 ${
                      photoUploaded ?
                      "border-growth bg-growth/5 text-growth" : "border-warm-border hover:border-primary hover:bg-primary-pale text-ink-muted hover:text-primary"}`
                      }>
                      
                        {photoUploaded ?
                      <>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span className="text-sm font-medium">Photo added</span>
                          </> :

                      <>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                            </svg>
                            <span className="text-sm font-medium">Click to upload a photo</span>
                            <span className="text-xs">JPG, PNG up to 5MB · private to your campaign</span>
                          </>
                      }
                      </button>
                    </div>
                  </div>
                </div>
              }

              {/* STEP 2: Goal */}
              {currentStep === 2 &&
              <div className="space-y-6">
                  <div>
                    <h2 className="font-heading font-semibold text-xl text-ink mb-1">Set your fundraising goal</h2>
                    <p className="text-sm text-ink-muted">You can always adjust this later. Your goal is only visible to people you invite.</p>
                  </div>

                  {/* Goal slider */}
                  <div className="space-y-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-heading font-bold text-primary">${goalAmount.toLocaleString()}</span>
                      <span className="text-ink-muted text-sm">USD</span>
                    </div>
                    <input
                    type="range"
                    min={1000}
                    max={80000}
                    step={500}
                    value={goalAmount}
                    onChange={(e) => setGoalAmount(Number(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{ accentColor: "#2D6A8F" }}
                    aria-label="Fundraising goal amount" />
                  
                    <div className="flex justify-between text-xs text-ink-soft">
                      <span>$1,000</span>
                      <span>$80,000</span>
                    </div>
                  </div>

                  {/* Cost breakdown */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-ink">Typical IVF cost breakdown</p>
                    {costBreakdown.map((item) =>
                  <div key={item.label} className="flex justify-between items-center py-2 border-b border-warm-border-light">
                        <span className="text-sm text-ink-muted">{item.label}</span>
                        <span className="text-sm font-semibold text-ink">${item.amount.toLocaleString()}</span>
                      </div>
                  )}
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-sm font-bold text-ink">Typical Total</span>
                      <span className="text-sm font-bold text-primary">${costBreakdown.reduce((s, i) => s + i.amount, 0).toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl" style={{ background: "#D4EAD9", border: "1px solid rgba(90,138,111,0.2)" }}>
                    <p className="text-xs text-growth font-medium flex items-start gap-2">
                      <span aria-hidden="true">✨</span>
                      BabyBank has <strong>no platform fee</strong> on the first $5,000 raised. Standard processing fees apply to donations.
                    </p>
                  </div>
                </div>
              }

              {/* STEP 3: Privacy */}
              {currentStep === 3 &&
              <div className="space-y-6">
                  <div>
                    <h2 className="font-heading font-semibold text-xl text-ink mb-1">Choose your privacy settings</h2>
                    <p className="text-sm text-ink-muted">Your campaign is <strong>never public</strong>. Choose how supporters access it.</p>
                  </div>

                  <div className="space-y-3">
                    {[
                  {
                    id: "link" as const,
                    title: "Private Link",
                    desc: "Share a unique, unguessable link via text or email. Anyone with the link can donate.",
                    icon: "🔗",
                    recommended: true
                  },
                  {
                    id: "email" as const,
                    title: "Email Invite Only",
                    desc: "Only people you explicitly invite by email can view and donate to your campaign.",
                    icon: "📧"
                  },
                  {
                    id: "code" as const,
                    title: "Access Code",
                    desc: "Supporters enter a private code you share. Adds an extra layer of security.",
                    icon: "🔒"
                  }].
                  map((option) =>
                  <button
                    key={option.id}
                    onClick={() => setPrivacyLevel(option.id)}
                    className={`w-full p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                    privacyLevel === option.id ?
                    "border-primary bg-primary-pale" : "border-warm-border bg-warm-bg hover:border-primary/40"}`
                    }>
                    
                        <div className="flex items-start gap-4">
                          <span className="text-2xl flex-shrink-0 mt-0.5" aria-hidden="true">{option.icon}</span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-heading font-semibold text-sm text-ink">{option.title}</span>
                              {option.recommended &&
                          <span className="px-2 py-0.5 rounded-full text-2xs font-semibold"
                          style={{ background: "#D4EAD9", color: "#3D6B52" }}>Recommended</span>
                          }
                            </div>
                            <p className="text-xs text-ink-muted leading-relaxed">{option.desc}</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                      privacyLevel === option.id ? "border-primary bg-primary" : "border-warm-border"}`
                      }>
                            {privacyLevel === option.id &&
                        <div className="w-2 h-2 rounded-full bg-white" />
                        }
                          </div>
                        </div>
                      </button>
                  )}
                  </div>

                  <div className="p-4 rounded-2xl" style={{ background: "#EBF4FA" }}>
                    <p className="text-xs text-primary font-medium">
                      🛡️ Your campaign will never appear in search engines, social media, or any public directory. Privacy is built into BabyBank's foundation.
                    </p>
                  </div>
                </div>
              }

              {/* STEP 4: Preview */}
              {currentStep === 4 &&
              <div className="space-y-6">
                  <div>
                    <h2 className="font-heading font-semibold text-xl text-ink mb-1">Campaign preview</h2>
                    <p className="text-sm text-ink-muted">Here's how your campaign will look to invited supporters.</p>
                  </div>

                  {/* Preview card */}
                  <div className="rounded-3xl overflow-hidden border border-warm-border shadow-warm-md">
                    {/* Preview header image */}
                    <div className="relative h-48 overflow-hidden" style={{ background: "linear-gradient(135deg, #EBF4FA, #FDF0ED)" }}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center space-y-2">
                          <div className="text-5xl" aria-hidden="true">
                            {treatmentTypes.find((t) => t.id === selectedTreatment)?.icon || "💛"}
                          </div>
                          <p className="text-xs text-ink-muted font-medium">
                            {treatmentTypes.find((t) => t.id === selectedTreatment)?.label || "Fertility Treatment"}
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{ background: "rgba(255,255,255,0.9)", color: "#2D6A8F" }}>
                          🔒 Private Campaign
                        </span>
                      </div>
                    </div>

                    <div className="p-6 bg-white space-y-4">
                      <h3 className="font-heading font-bold text-lg text-ink">
                        {campaignName || "Your Campaign Name"}
                      </h3>
                      <p className="text-sm text-ink-muted leading-relaxed">
                        {story || "Your personal story will appear here — only visible to the people you invite."}
                      </p>

                      {/* Progress */}
                      <div className="space-y-2 pt-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-bold text-primary">$0 raised</span>
                          <span className="text-ink-muted">of ${goalAmount.toLocaleString()} goal</span>
                        </div>
                        <div className="h-3 rounded-full overflow-hidden" style={{ background: "#EDE5DB" }}>
                          <div className="h-full rounded-full" style={{ width: "0%", background: "linear-gradient(90deg, #2D6A8F, #5A8A6F)" }} />
                        </div>
                        <p className="text-xs text-ink-soft">0% funded · 0 supporters</p>
                      </div>

                      <button className="btn-primary w-full justify-center text-sm py-3">
                        Contribute to This Journey 💛
                      </button>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                  { label: "Treatment", value: treatmentTypes.find((t) => t.id === selectedTreatment)?.label || "—" },
                  { label: "Goal", value: `$${goalAmount.toLocaleString()}` },
                  { label: "Privacy", value: privacyLevel === "link" ? "Private Link" : privacyLevel === "email" ? "Email Only" : "Access Code" },
                  { label: "Status", value: "Draft" }].
                  map(({ label, value }) =>
                  <div key={label} className="p-4 rounded-2xl" style={{ background: "#FDF8F4", border: "1px solid #EDE5DB" }}>
                        <p className="text-xs text-ink-muted mb-1">{label}</p>
                        <p className="font-heading font-semibold text-sm text-ink">{value}</p>
                      </div>
                  )}
                  </div>

                  <div className="p-4 rounded-2xl border border-warm-border-light" style={{ background: "#FDF0ED" }}>
                    <p className="text-xs text-accent-dark font-medium">
                      🎉 You're almost there! After launching, you'll receive a private link to share with friends and family. No credit card needed to start.
                    </p>
                  </div>
                </div>
              }

              {/* Navigation buttons */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-warm-border-light">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  currentStep === 0 ?
                  "opacity-0 pointer-events-none" : "text-ink-muted hover:text-ink hover:bg-warm-surface"}`
                  }>
                  
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>

                {currentStep < steps.length - 1 ?
                <button
                  onClick={handleNext}
                  className="btn-primary text-sm px-8 py-3">
                  
                    Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button> :

                <Link href="/sign-up" className="btn-primary text-sm px-8 py-3">
                    Launch Campaign
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                }
              </div>
            </div>
          </div>

          {/* Right: Sidebar */}
          <div className="space-y-6">
            {/* Privacy assurance */}
            <div className="bg-white rounded-3xl p-6 border border-warm-border-light shadow-warm-sm space-y-4">
              <h3 className="font-heading font-semibold text-base text-ink flex items-center gap-2">
                <span aria-hidden="true">🔒</span> Privacy Promise
              </h3>
              <ul className="space-y-3">
                {[
                "Never appears in search engines",
                "No public profile or listing",
                "You control who sees it",
                "Anonymous donation option",
                "HIPAA-aware data handling"].
                map((item) =>
                <li key={item} className="flex items-start gap-2.5 text-sm text-ink-muted">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5A8A6F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {item}
                  </li>
                )}
              </ul>
            </div>

            {/* Sample campaign */}
            <div className="bg-white rounded-3xl overflow-hidden border border-warm-border-light shadow-warm-sm">
              <div className="relative h-32 overflow-hidden">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_10b1248a6-1772160072055.png"
                  alt="Couple holding hands during a walk at sunset, representing hope and partnership"
                  fill
                  className="object-cover" />
                
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(28,43,53,0.6) 100%)" }} />
                <div className="absolute bottom-3 left-4">
                  <p className="text-white text-xs font-medium">Sample Campaign</p>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm font-heading font-semibold text-ink">Jessica & Tom's IVF Journey</p>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-primary font-bold">$14,200 raised</span>
                    <span className="text-ink-muted">of $20,000</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: "#EDE5DB" }}>
                    <div className="h-full rounded-full" style={{ width: "71%", background: "linear-gradient(90deg, #2D6A8F, #5A8A6F)" }} />
                  </div>
                </div>
                <p className="text-xs text-ink-muted">18 supporters · 🔒 Private link</p>
              </div>
            </div>

            {/* Support */}
            <div className="p-5 rounded-2xl" style={{ background: "linear-gradient(135deg, #EBF4FA, #D4EAD9)", border: "1px solid rgba(45,106,143,0.15)" }}>
              <p className="text-xs font-semibold text-ink mb-2">Need help?</p>
              <p className="text-xs text-ink-muted mb-3 leading-relaxed">Our compassionate support team is here for you during every step of your journey.</p>
              <a href="mailto:support@babybank.com" className="text-xs font-semibold text-primary hover:underline">
                support@babybank.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>);

}